import {Booking, BookingBoxes, BookingOptions, getOccupancyBooking} from "./booking";
import {
    Badge,
    classNames,
    Dialog,
    layout,
    PushButton,
    theme,
    typography,
    useAsync,
    useCountdown,
    useIdle,
    usePlatform,
    usePreventAutoDeactivate,
    useStorageItemValue,
    View,
    ViewContent
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {BookingViewHeader} from "./BookingViewHeader";
import {defined, findBoxesByNumbers} from "@variocube/app-common";
import {Box, Occupancy, Platform} from "@variocube/app-api";
import {OccupancyItem} from "./OccupancyItem";
import {DateTime} from "luxon";
import {useLocalization} from "../../i18n";

export interface BookingViewProps {
    booking: Booking | string;
    onDone: () => any;
    onProlong: (occupancy: Occupancy) => any;
    className?: string;
}

export function BookingView({booking, onDone, onProlong, className}: BookingViewProps) {
    const [occupancies, setOccupancies] = useState<Occupancy[]>();
    const [allOpen, setAllOpen] = useState(false);
    const platform = usePlatform();
    const bookingOptions = useStorageItemValue<BookingOptions>("bookingOptions");
    const {t} = useLocalization();
    const occupyLock = useRef(false);

    const openBoxes = useMemo(() => new Set<string>(), []);

    const idle = useIdle(30);
    const left = useCountdown(10, idle || allOpen);

    const bookingCode = isBooking(booking) ? booking.bookingCode : booking;

    usePreventAutoDeactivate();

    useEffect(() => {
        if (left <= 0) {
            onDone();
        }
    }, [left, onDone]);

    const {loading} = useAsync(async () => {
        setOccupancies(await platform.getOccupancies(bookingCode));
    }, [platform, bookingCode]);

    const neighborOccupancies = useMemo(() => {
        return isBooking(booking)
            ? booking.occupancies.filter(bookingOccupancy => !bookingOccupancy.ended && bookingOccupancy.cube != platform.hostname)
            : [];
    }, [booking]);
    const lastUsedBoxes = useMemo(() => {
        return isBooking(booking)
            ? booking.occupancies.filter(bookingOccupancy => bookingOccupancy.ended && bookingOccupancy.cube == platform.hostname)
                .map(bookingOccupancy => bookingOccupancy.boxNumber)
            : [];
    }, [booking]);

    const hasNeighborOccupancies = neighborOccupancies && neighborOccupancies.length > 0;

    const timeframe = isBooking(booking) ? booking : getAnyOccupancyBooking(occupancies);
    console.log("timeframe", timeframe);
    const expired = timeframe && new Date() >= new Date((timeframe as Booking).until);
    const started = timeframe && new Date() >= new Date((timeframe as Booking).from);
    const canceled = isBooking(booking) && booking.state === 'Canceled';
    const notStarted = defined(started) && !started;

    const canOpen = useMemo(() => {
        // grace period evaluation
        // allow access until the next `accessibleFrom` time independent of the tenant
        // if `accessibleFrom` is not set, default to 00:00
        const accessibleFrom = bookingOptions?.accessibleFrom || '00:00';

        const [hour, minute] = accessibleFrom.split(':');
        try {
            const until = DateTime.fromISO((timeframe as Booking).until)
                .plus({ days: 1 })
                .set({ hour: parseInt(hour), minute: parseInt(minute) });
            console.log('until', until.toString())
            return until >= DateTime.now();
        } catch (err) {
            console.error('failed to parse grace period of until', err);
        }

        return started && !expired;
    }, [timeframe, bookingOptions, started, !expired]);

    const canOccupy = started && !expired && !canceled;

    const occupy = useAsync(async () => {
        if (isBooking(booking) && canOccupy && occupancies && bookingOptions) {

            // prevent re-entry if storage changes and this is already running
            if (!occupyLock.current) {
                // acquire lock
                occupyLock.current = true;

                try {
                    const occupied = [
                        ...occupancies.map(getOccupancyBooking).filter(defined).map(b => b.boxType),
                        ...neighborOccupancies.map(o => o.boxType),
                    ];

                    const required = {...booking.boxes};
                    for (const boxType of occupied) {
                        required[boxType]--;
                    }

                    const requiredCount = Object.values(required)
                        .reduce((a, b) => a + b, 0);

                    if (requiredCount > 0) {
                        const newOccupancies = await occupyBookedBoxes(platform, booking, required, lastUsedBoxes);

                        if (newOccupancies.length > 0) {
                            setOccupancies([...occupancies, ...newOccupancies]);
                        }

                        return {
                            failed: newOccupancies.length != requiredCount,
                            otherCubes: (bookingOptions.siteCubes ?? []).length > 1,
                        };
                    }
                }
                finally {
                    // release lock
                    occupyLock.current = false;
                }
            }
        }
    }, [platform, booking, occupancies, bookingOptions, neighborOccupancies, canOccupy]);

    const handleProlongBooking = useCallback(() => {
        if (occupancies && occupancies.length == 1) {
            onProlong(occupancies[0]);
        }
    }, [occupancies, onProlong]);

    const handleBoxOpenChanged = useCallback((boxNumber: string, open: boolean) => {
        openBoxes[open ? "add" : "delete"](boxNumber);
        setAllOpen(openBoxes.size == occupancies?.length);
    }, [openBoxes, occupancies]);

    const resetAllOpen = useCallback(() => setAllOpen(false), []);

    return (
        <View className={className}>
            <BookingViewHeader title={t("YourLockers")} onClose={onDone}/>
            {hasNeighborOccupancies && (
                <div className={classNames(theme.moreContrast)}>
                    <div className={classNames(layout.paddingMedium, typography.title)}>
                        {t("NeighborOccupancies")}
                    </div>
                </div>
            )}
            {expired && (
                <div className={classNames(theme.moreContrast, layout.horizontal, layout.center)}>
                    <div className={classNames(layout.paddingMedium, typography.title)}>
                        <Badge error>{t("BookingPeriodEnded")}</Badge>
                    </div>
                    {occupancies?.length == 1 && (
                        <div className={classNames(layout.paddingMedium)}>
                            <PushButton onClick={handleProlongBooking}>
                                {t("ProlongBooking")}
                            </PushButton>
                        </div>
                    )}
                </div>
            )}
            {notStarted && (
                <div className={classNames(layout.fill, layout.vertical, layout.center, layout.alignCenter)}>
                    <Badge error>{t("BookingPeriodNotStarted")}</Badge>
                </div>
            )}
            {occupy.result?.failed && (
                <div className={classNames(theme.moreContrast)}>
                    <div className={classNames(layout.paddingMedium)}>
                        {occupy.result?.otherCubes && (
                            <Badge warning>{t("InsufficientBoxQuantityOtherCubes")}</Badge>
                        )}
                        {!occupy.result?.otherCubes && (
                            <Badge error>{t("InsufficientBoxQuantity")}</Badge>
                        )}
                    </div>
                </div>
            )}

            {(loading || occupy.loading) && (
                <div className={classNames(layout.fill, layout.vertical, layout.center, layout.alignCenter)}>
                    <Badge>{t("LoadingLockers")}</Badge>
                </div>
            )}

            {occupancies && !loading && !occupy.loading && !expired && (
                <ViewContent className={classNames(layout.horizontalWrap, layout.center, layout.alignCenter)}>
                    {occupancies.map(occupancy => (
                        <OccupancyItem
                            key={occupancy.uuid}
                            occupancy={occupancy}
                            canOpen={!!canOpen}
                            onBoxOpenChanged={handleBoxOpenChanged}
                        />
                    ))}
                    {(occupancies.length == 0) && (
                        <Badge>{t("NoLockersFound")}</Badge>
                    )}
                </ViewContent>
            )}
            <Dialog open={idle || allOpen} onBackdropClick={resetAllOpen}>
                <div className={classNames(layout.paddingMedium, typography.alignCenter)}>
                    {t(idle ? "AutoCloseInactive" : "AutoCloseAllOpen", {seconds: left})}
                </div>
                <div className={classNames(theme.lessContrast, layout.paddingMedium, typography.alignCenter)}>
                    <PushButton onClick={resetAllOpen}>
                        {t("Cancel")}
                    </PushButton>
                </div>
            </Dialog>
        </View>
    );
}


function isBooking(booking: Booking | string): booking is Booking {
    return typeof booking != "string";
}

function getAnyOccupancyBooking(occupancies?: Occupancy[]) {
    return occupancies?.map(getOccupancyBooking)
        .filter(defined)
        .pop();
}

async function occupyBookedBoxes(platform: Platform, booking: Booking, boxes: BookingBoxes, preferredBoxNumbers: string[]) {
    const preferredBoxes = await findBoxesByNumbers(platform, preferredBoxNumbers);
    const newOccupancies = [];
    for (const [boxType, quantity] of Object.entries(boxes)) {

        const preferred = preferredBoxes.filter(box => box.types.indexOf(boxType) >= 0);

        for (let i = 0; i < quantity; i++) {
            try {
                const occupancy = await tryOccupyAny(platform, booking, preferred, boxType)
                await platform.confirmOccupancy(occupancy.uuid);
                newOccupancies.push(occupancy);
            }
            catch (error) {
                console.error(`Couldn't occupy box with type (${boxType}) for booking (${booking.bookingCode}), error: `, error);
            }
        }
    }
    return newOccupancies;
}

async function tryOccupyAny(platform: Platform, booking: Booking, boxes: Box[], boxType: string) {
    const request = {
        accessCode: booking.bookingCode,
        content: {
            booking: {
                boxType,
                name: booking.name,
                phone: booking.phone,
                email: booking.email,
                from: booking.from,
                until: booking.until
            },
            bookingUuid: booking.uuid,
            bookingCode: booking.bookingCode
        },
    }
    for (const box of boxes) {
        try {
            return await platform.occupy({
                ...request,
                boxNumber: box.number
            });
        }
        catch (error) {
            // continue
        }
    }
    return await platform.occupy({
        ...request,
        type: boxType,
    });
}